import { useTheme } from "../../theme/useTheme"
import { Footer } from "./Footer"
import { Box, Paper } from "@mui/material"
import React, { type ReactNode } from "react"

interface PageLayoutProps {
    children: ReactNode
    currentPage?: number
    totalPages?: number
    isLastPage?: boolean
}

// mobile display zoom, same ratio as the desktop sheet zoom in ThemeProvider.
// The sheet stays width:100% (Chrome resolves the zoomed element's own
// percentages pre-zoom, so it still fills the viewport exactly); print
// re-sets everything and forces zoom back to 1
const MOBILE_ZOOM = 1.3
// nominal viewport width the sheet floor derives from: (280 - 18) / 1.3 ≈ a
// 262px rendered floor. Inert at 280 and wider, where the fluid width:100%
// already exceeds it; binds only below, where it stops the sheet collapsing
// and the viewport pans instead
const MOBILE_SHEET_FLOOR_NOMINAL = 280

export const PageLayout: React.FC<PageLayoutProps> = ({
    children,
    currentPage = 1,
    totalPages = 1,
    isLastPage = false,
}) => {
    const { themeSettings, layout, breakpoints } = useTheme()

    return (
        <Paper
            className="page"
            elevation={5}
            sx={{
                border: 1,
                borderRadius: 0,
                borderColor: themeSettings.borderColor,
                width: !breakpoints.isFullPage ? "100%" : `${layout.pageWidth}px`,
                minWidth: `${(MOBILE_SHEET_FLOOR_NOMINAL - 18) / MOBILE_ZOOM}px`,
                maxWidth: `${layout.pageWidth}px`,
                zoom: !breakpoints.isFullPage ? MOBILE_ZOOM : undefined,
                height: !breakpoints.isFullPage ? "auto" : `${layout.pageHeight}px`,
                overflow: "hidden",
                margin: `${!breakpoints.isFullPage ? 8 : 36}px auto`,
                marginBottom: isLastPage ? "36px" : `${!breakpoints.isFullPage ? 8 : 36}px`,
                padding: !breakpoints.isFullPage
                    ? // ~4.5% of sheet width, matching print's 36/794; print
                      // re-sets fixed padding
                      `${layout.pagePadding}px 4%`
                    : `${layout.pagePadding}px`,
                backgroundImage: "none",
                backgroundColor: themeSettings.surfaceColor,
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <Box sx={{ flex: 1, overflow: "hidden" }}>{children}</Box>
                <Footer currentPage={currentPage} totalPages={totalPages} />
            </Box>
        </Paper>
    )
}
